import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { SortingState } from '@hub/libs/bearing-hub.helper';
import { DimensionsHeader } from '@hub/bearing-hub/BearingHubPage/BearingsTable/DimensionsHeader';
import type { HeaderDefinition, Headers } from './useBearingsTableStore';
import { useBearingsTableStore } from './useBearingsTableStore';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api';
import type { DimensionKey } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { UnitSystem } from '@igus/kopla-domain';

export interface BearingHubPageProps {
  selectedShape: ShapeDescriptor;
  lastKnownValidResponse: IbhPartsResponseDto[];
  unitSystem: UnitSystem;
  clearSelections: () => void;
  selectedParts: string[];
  isFullScreen?: boolean;
  setActiveModal?: (state: boolean) => void;
}

export const BearingsTableHead = ({
  selectedShape,
  lastKnownValidResponse,
  setActiveModal,
  unitSystem,
  isFullScreen,
  clearSelections,
  selectedParts,
}: BearingHubPageProps) => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const { tableHeaders, setTableHeadersSortingState, allShapeDimensionsHeadersKeys } = useBearingsTableStore();

  const tableHeadersContent: Partial<Record<Headers, string | ReactNode>> = {
    image: '',
    material: t(`HEADLINES.MATERIAL`, { ns: 'bearing-hub/bearingHub' }),
    dimensions: (
      <DimensionsHeader
        selectedShape={selectedShape}
        text={t(`HEADLINES.DIMENSIONS`, { ns: 'bearing-hub/bearingHub' })}
        unitSystem={unitSystem}
        isFullScreen={isFullScreen}
      />
    ),
    deliveryTime: t(`HEADLINES.DELIVERY_TIME`, { ns: 'bearing-hub/bearingHub' }),
    lifetime: (
      <div className="flex">
        <p>{t(`HEADLINES.LIFETIME`, { ns: 'bearing-hub/bearingHub' })}</p>
        <div
          className="ml-2"
          data-testid="show-life-time-modal"
          onClick={event => {
            event.stopPropagation();
            setActiveModal?.(true);
          }}
        >
          <Icon size={6} id={IconID.INFO_CIRCLE_GRAY} />
        </div>
      </div>
    ),
    price: `${t(`HEADLINES.PRICE`, { ns: 'bearing-hub/bearingHub' })} / ${t(`HEADLINES.PIECE`, {
      ns: 'bearing-hub/bearingHub',
    })}`,
    // eslint-disable-next-line no-nested-ternary
    selection: selectedParts.length ? (
      <span
        onClick={() => {
          clearSelections();
        }}
      >
        <Icon size={10} color={IconColor.WHITE} id={IconID.CHECK_BOX_INDETERMINATE} />
      </span>
    ) : (
      <span>
        <Icon size={10} color={IconColor.WHITE} id={IconID.CHECK_BOX_OUTLINE_BLANK} />
      </span>
    ),
  };

  const [isSticky, setIsSticky] = useState(false);

  const getHeaderStyles = (key: Headers): string => {
    const headerWidth: { [key in Headers]?: string } = {
      image: 'min-w-[72px]',
      material: 'w-[125px]',
      dimensions: 'min-w-[270px]',
      selection: 'max-w-[60px]',
    };
    return headerWidth[key];
  };

  const onSort = (header: HeaderDefinition): void => {
    if (!header.sorting) {
      return;
    }
    setTableHeadersSortingState(header.key);
  };

  const onScroll = useCallback(() => {
    const headerElem = window.document.getElementById('product-table');
    if (typeof headerElem?.getBoundingClientRect().top !== 'undefined') {
      if (
        window.pageYOffset > headerElem.getBoundingClientRect().top - 10 + document.documentElement.scrollTop &&
        lastKnownValidResponse.length > 10
      ) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    }
  }, [lastKnownValidResponse]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  return (
    <thead
      id="sticky-header"
      className={`bg-black text-left text-sm ${isSticky ? `sticky top-0 z-30` : ''}`}
      data-testid="table-header"
    >
      <tr
        className="leading-[4rem] border border-black border-t-0 h-[68px] hidden xl:table-row"
        data-testid="table-header-container"
      >
        {tableHeaders
          .filter(h => !allShapeDimensionsHeadersKeys.includes(h.key as DimensionKey))
          .map((header, index) => (
            <th
              key={header.key}
              data-testid={`cell-header-${header.key}`}
              className={`${
                header.key === 'deliveryTime' ? 'whitespace-normal leading-[2rem]' : 'whitespace-nowrap'
              } px-3  ${
                header.sortingState === SortingState.NoSort
                  ? 'text-white font-normal'
                  : 'text-igus-primary font-medium select-none'
              } ${getHeaderStyles(header.key)}`}
            >
              <div
                className={`${header.key === 'price' ? 'justify-end' : ''} flex items-center ${
                  header.sorting ? 'cursor-pointer' : 'cursor-default'
                } group transition-all w-full`}
                onClick={() => onSort(header)}
                onKeyUp={() => null}
                role="button"
                tabIndex={index}
              >
                <div className="w-full">{tableHeadersContent[header.key]}</div>{' '}
                {header.sorting && (
                  <div
                    data-testid={`sort-icon-container-${header.key}`}
                    className={`ml-2 flex mt-1 ${
                      header.sortingState === SortingState.NoSort ? 'invisible' : 'visible'
                    } ${header.sortingState === SortingState.Descending ? 'rotate-180' : 'rotate-0'}`}
                  >
                    <Icon size={5} id={IconID.ARROW_TABLE_SORTING} />
                  </div>
                )}
              </div>
            </th>
          ))}
      </tr>
      <tr className="leading-[4rem] border border-black border-t-0 h-[68px] table-row xl:hidden">
        {tableHeaders
          .filter(
            header => header.key !== 'lifetime' && !allShapeDimensionsHeadersKeys.includes(header.key as DimensionKey)
          )
          .map((header, index) => (
            <th
              key={header.key}
              data-testid={`small-cell-header-${header.key}`}
              className="text-white font-normal leading-one px-2"
              onClick={header.key === 'deliveryTime' ? undefined : () => onSort(header)}
              onKeyUp={header.key === 'deliveryTime' ? undefined : () => null}
              role="button"
              colSpan={header.key === 'deliveryTime' ? 2 : 1}
            >
              <div
                className={`${header.key === 'price' ? 'justify-end' : ''} flex items-center ${
                  header.sorting ? 'cursor-pointer' : 'cursor-default'
                } group transition-all`}
                tabIndex={index}
              >
                {header.key === 'deliveryTime' ? (
                  <>
                    <div className="flex flex-col">
                      <div className="leading-normal whitespace-nowrap">
                        <span
                          className={
                            header.sortingState !== SortingState.NoSort
                              ? 'text-igus-primary font-medium select-none'
                              : 'text-white font-normal'
                          }
                        >
                          {tableHeadersContent[header.key]}
                        </span>{' '}
                        /
                      </div>
                      <div className="leading-normal whitespace-nowrap flex">
                        <div
                          className={
                            header.key === 'deliveryTime' &&
                            tableHeaders.find(headerItem => headerItem.key === 'lifetime')?.sortingState !==
                              SortingState.NoSort
                              ? 'text-igus-primary font-medium select-none'
                              : 'text-white font-normal'
                          }
                        >
                          {tableHeadersContent[tableHeaders.find(headerItem => headerItem.key === 'lifetime')?.key]}
                        </div>
                      </div>
                    </div>
                    {header.sorting && (
                      <div
                        data-testid={`sort-icon-container-${header.key}`}
                        className={`ml-2 flex mt-1 ${
                          header.sortingState === SortingState.NoSort &&
                          tableHeaders.find(headerItem => headerItem.key === 'lifetime')?.sortingState ===
                            SortingState.NoSort
                            ? 'invisible'
                            : 'visible'
                        } ${
                          header.sortingState === SortingState.Descending ||
                          tableHeaders.find(headerItem => headerItem.key === 'lifetime')?.sortingState ===
                            SortingState.Descending
                            ? 'rotate-180'
                            : 'rotate-0'
                        }`}
                      >
                        <Icon size={5} id={IconID.ARROW_TABLE_SORTING} />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div
                      className={`whitespace-nowrap ${
                        header.sortingState !== SortingState.NoSort
                          ? 'text-igus-primary font-medium select-none'
                          : 'text-white font-normal'
                      }`}
                    >
                      {tableHeadersContent[header.key]}
                    </div>
                    {header.sorting && (
                      <div
                        data-testid={`sort-icon-container-${header.key}`}
                        className={`ml-2 flex mt-1 ${
                          header.sortingState === SortingState.NoSort ? 'invisible' : 'visible'
                        } ${header.sortingState === SortingState.Descending ? 'rotate-180' : 'rotate-0'}`}
                      >
                        <Icon size={5} id={IconID.ARROW_TABLE_SORTING} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </th>
          ))}
      </tr>
    </thead>
  );
};
